import { useCallback } from "react"
import { useMutation } from "urql"

import "@/screens/Accounting/subfeatures/PayableApprovals/graphql/changeInvoiceStatus.mutation"

import { type MutationLockResult, MutationLockResultStatus } from "@/entityLock/types"
import { toEntityLock } from "@/entityLock/utils"
import type {
  ChangeInvoiceStatusMutation,
  ChangeInvoiceStatusMutationVariables,
  InvoiceStatusChangeFailure,
  InvoiceStatusChangeInput,
} from "@/graphql/codegen/graphql"
import { ChangeInvoiceStatusDocument, InvoiceStatus } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export type VoidInvoiceInput = Pick<InvoiceStatusChangeInput, "invoiceId"> & { comment: string }

export interface UseVoidInvoiceReturn {
  voidInvoice: (input: VoidInvoiceInput) => Promise<MutationLockResult>
  isVoiding: boolean
}

export const useVoidInvoice = (): UseVoidInvoiceReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    ChangeInvoiceStatusMutation,
    ChangeInvoiceStatusMutationVariables
  >(ChangeInvoiceStatusDocument)

  const voidInvoice = useCallback(
    async (input: VoidInvoiceInput): Promise<MutationLockResult> => {
      const { data, error } = await executeMutation({
        input: { invoiceId: input.invoiceId, action: InvoiceStatus.Voided, comment: input.comment },
      })
      const output = data?.invoice.statusChange

      if (output?.__typename === "EntityLockFailure") {
        const currentLock = toEntityLock(output.error.currentLock)!
        return {
          status: MutationLockResultStatus.LockConflict,
          currentLock,
          message: output.error.message,
        }
      }

      const hasError = error || output?.__typename === "InvoiceStatusChangeFailure"

      if (hasError) {
        const message = error?.message ?? (output as InvoiceStatusChangeFailure).error.message
        notify({ type: NotificationType.Error, message })
        return { status: MutationLockResultStatus.Error }
      }

      notify({ type: NotificationType.Success, message: "Invoice voided" })
      return { status: MutationLockResultStatus.Success }
    },
    [executeMutation, notify]
  )

  return { voidInvoice, isVoiding: fetching }
}
